import React, { useMemo, useEffect } from 'react';
import Script from 'next/script';
import { useGlobalData } from 'lib/hooks/sitecore/useGlobalData';
import { useSiteSettings } from 'lib/hooks/useSiteSettings';
import { Data } from '@generated/Foundation.GMI.model';
import { useDisabledFeatureFlags } from 'lib/hooks/useDisabledFeatureFlags';
import { pushToDataLayer } from 'components/helpers/Global/Analytics/DataLayer';

declare global {
  interface Window {
    __bvConfigured?: boolean;
  }
}

export interface BazaarVoiceScriptProps {
  strategy?: 'beforeInteractive' | 'afterInteractive' | 'lazyOnload' | 'worker';
}

// Docs for BV callbacks: https://docs.bazaarvoice.com/articles/ratings-reviews/integrate-event-callbacks
// FYI - The documentation has the casing wrong in several places
export interface BazaarVoiceEventData {
  Id: string;
  TotalAnswerCount?: number;
  TotalQuestionCount?: number;
  TotalReviewCount?: number;
  TotalContentCount: number;
  BVProduct: 'swat_reviews' | 'swat_questions' | 'inpage_submission';
  contentType?: 'review' | 'question' | 'answer';
  SubmittedStep?:
    | 'mandatory'
    | 'photoVideo'
    | 'personalInformation'
    | 'productrating'
    | 'brandDetails';
}

export function BazaarVoiceScript({ strategy = 'lazyOnload' }: BazaarVoiceScriptProps) {
  const { siteName: site, environment, locale } = useGlobalData();
  const siteSettings = useSiteSettings();

  const disabledFeatures = useDisabledFeatureFlags();

  useEffect(() => {
    const start = Date.now();
    const MAX_WAIT = 5_000; // 5 seconds

    const poll = window.setInterval(() => {
      // stop if timed out
      if (Date.now() - start > MAX_WAIT) {
        window.clearInterval(poll);
        return;
      }

      // stop if already configured
      if (window.__bvConfigured) {
        window.clearInterval(poll);
        return;
      }

      if (!window.BV) return;

      window.__bvConfigured = true;

      window.BV.configure('global', {
        events: {
          submissionSubmitted: function (data: BazaarVoiceEventData) {
            // Send GTM events based on content type
            if (data.contentType === 'review') {
              pushToDataLayer({
                event: 'review',
                review_step: data.SubmittedStep,
              });
            }

            if (data.contentType === 'question') {
              pushToDataLayer({
                event: 'question',
              });
            }

            if (data.contentType === 'answer') {
              pushToDataLayer({
                event: 'question_answer',
              });
            }
          },
        },
      });

      window.clearInterval(poll);
    }, 250);

    return () => window.clearInterval(poll);
  }, []);

  // Memoize the BazaarVoice configuration to avoid recalculations on every render
  const bazaarVoiceConfig = useMemo(() => {
    const bvFields = siteSettings?.bazaarVoiceSetting
      ?.fields as Data.Settings.BazaarVoiceSettings['fields'];

    const {
      bazaarVoiceBrandOverride,
      bazaarVoiceEnvironmentOverride,
      bazaarVoiceLanguageOverride,
    } = bvFields || {};

    // Format brand name for BazaarVoice (lowercase, no spaces)
    const brand =
      bazaarVoiceBrandOverride?.value || site?.toLowerCase().replace(/\s+/g, '') || 'default';

    // Format environment for BazaarVoice
    const bvEnvironment =
      bazaarVoiceEnvironmentOverride?.value ||
      (environment === 'Production' ? 'production' : 'staging');

    // Format locale for BazaarVoice (replace hyphens with underscores)
    const bvLocale = bazaarVoiceLanguageOverride?.value || (locale?.replace('-', '_') ?? 'en_US');

    return {
      brand,
      environment: bvEnvironment,
      locale: bvLocale,
    };
  }, [site, environment, locale, siteSettings?.bazaarVoiceSetting?.fields]);

  // Early return if BV is disabled from feature flags
  if (disabledFeatures?.BV) {
    return <></>;
  }

  // Early return if essential data is missing
  if (!bazaarVoiceConfig.brand || !bazaarVoiceConfig.environment || !bazaarVoiceConfig.locale) {
    console.warn('BazaarVoiceScript: Missing required configuration data');
    return <></>;
  }

  const scriptUrl = `https://apps.bazaarvoice.com/deployments/${bazaarVoiceConfig.brand}/main_site/${bazaarVoiceConfig.environment}/${bazaarVoiceConfig.locale}/bv.js`;

  return (
    <Script
      id={`bazaarvoice-script-${bazaarVoiceConfig.brand}-${bazaarVoiceConfig.environment}-${bazaarVoiceConfig.locale}`}
      src={scriptUrl}
      strategy={strategy}
      onError={(e) => {
        console.warn('BazaarVoiceScript: Failed to load script', e);
      }}
    />
  );
}
